import { format } from 'date-fns';
import { isEmpty, keyBy } from 'lodash';
import AIRLINES_VN from '@/mappings/airlines_vn.json';
import AIRPORT_VN from '@/mappings/airport_vn.json';
import { FlightData, Segment } from '@/slices/flightData/slices';

export interface MappingFlightCardResponse {
  departureTime: string;
  departureAirportName: string;
  arrivalAirportName: string;
  departureCity: string;
  arrivalCity: string;
  totalPriceAdult: number;
  totalPriceAdultOriginal: number;
  discountAmount: number;
  arrivalTime: string;
  duration: number;
  airlineCode: string;
  airlineName: string;
  fareDataId: number;
  departureDate?: Date | null;
  arrivalDate?: Date | null;
}

export interface IFlightDataMappingSegments {
  departureTime: string;
  departureAirportName: string;
  arrivalAirportName: string;
  departureCity: string;
  arrivalCity: string;
  totalPriceAdult: number;
  arrivalTime: string;
  duration: number;
  airlineCode: string;
  airlineName: string;
  stopDuration: number;
  operatingAirline: string;
  dayChange: number;
  class?: string;
  startTime?: string;
}

export const getFlightPrice = (
  flightData: FlightData,
) => (
  flightData?.route.schedules[0].totalPriceAdult - flightData?.route.schedules[0].discountCheapest1
);

export const getMappingFlightCardData = (
  flightData: FlightData,
): MappingFlightCardResponse => ({
  departureTime: flightData?.time,
  arrivalTime: flightData?.arrivalTime,
  departureAirportName: flightData?.route.from.name,
  departureCity: flightData?.route.from.cityName,
  arrivalCity: flightData?.route.to.cityName,
  arrivalAirportName: flightData?.route.to.name,
  duration: flightData?.duration,
  totalPriceAdult: flightData?.route.schedules[0].totalPriceAdult,
  totalPriceAdultOriginal:
    flightData?.route.schedules[0].totalPriceAdultOriginal,
  discountAmount: flightData?.route.schedules[0].discountAmount,
  airlineCode: flightData?.company?.code,
  airlineName: flightData?.company.name,
  fareDataId: flightData?.fareDataId,
  departureDate: flightData?.date ? new Date(flightData?.date) : null,
  arrivalDate: flightData?.arrivalDate
    ? new Date(flightData?.arrivalDate)
    : null,
});

export const getMappingDataFromSegments = (
  flightData: any,
): any[] => {
  const AIRLINE_VN_OBJ: any = AIRLINES_VN;
  const AIRPORT_VN_CODE = keyBy(AIRPORT_VN.airports, 'AirportCode');
  if (isEmpty(flightData)) {
    return [];
  }
  return (flightData.segments || flightData.listSegment || []).map((segment: any) => ({
    ...segment,
    departureTime: format(new Date(segment.startTime), 'HH:mm'),
    departureDate: new Date(segment.startTime),
    arrivalDate: new Date(segment.endTime),
    arrivalTime: format(new Date(segment.endTime), 'HH:mm'),
    departureAirportName:
      AIRPORT_VN_CODE?.[segment?.startPoint]?.AirportName_Vi,
    departureCity: AIRPORT_VN_CODE?.[segment?.startPoint]?.CityName_Vi,
    arrivalCity: AIRPORT_VN_CODE?.[segment?.endPoint]?.CityName_Vi,
    arrivalAirportName: AIRPORT_VN_CODE?.[segment?.endPoint]?.AirportName_Vi,
    duration: segment.duration || flightData.duration,
    totalPriceAdult: flightData?.route?.schedules[0].totalPriceAdult || 0,
    airlineCode: segment.airline,
    airlineName: AIRLINE_VN_OBJ[segment.airline as string]?.name || segment.airline,
    stopDuration: segment.stopTime,
    operatingAirline: segment.operatingAirline,
    dayChange: segment.dayChange,
    class: segment.class,
  }));
};

export const getSegmentsDataFromOrder = ({
  orderData,
  type,
}: {
  orderData: any;
  type: 'depart' | 'return';
}) => {
  if (!orderData) return [];
  const sortOrderData = orderData.length > 1
    ? [...orderData]?.sort(
      (x: any, y: any) => {
        if (x.tickets[0].departureTime > y.tickets[0].departureTime) {
          return 1;
        }
        return -1;
      },
    )
    : orderData;

  const isRoundTripSameAirline = sortOrderData.length === 1
    && sortOrderData[0].tickets?.[0]?.route.split('|')?.length > 1;
  const isRoundTrip = sortOrderData.length === 2 || isRoundTripSameAirline;

  let returnTicketInfo;
  let departTicketInfo;

  if (!isRoundTrip) {
    departTicketInfo = sortOrderData[0].tickets[0].info
      && JSON.parse(sortOrderData[0].tickets[0].info);
    return getMappingDataFromSegments({
      segments: departTicketInfo?.listSegment,
    } as any);
  }

  if (isRoundTripSameAirline) {
    const ticket = sortOrderData[0].tickets[0];
    const keyCodeDepart = ticket?.route?.split('|')?.[0];
    const keyCodeReturn = ticket?.route?.split('|')?.[1];
    if (type === 'depart') {
      departTicketInfo = sortOrderData[0].tickets.find(
        (t: any) => t?.fromLocation + t.toLocation === keyCodeDepart,
      );
      const info = departTicketInfo && departTicketInfo.info && JSON.parse(departTicketInfo.info);
      return getMappingDataFromSegments({
        segments: info?.listSegment,
      } as any);
    }
    returnTicketInfo = sortOrderData[0].tickets.find(
      (t: any) => t?.fromLocation + t.toLocation === keyCodeReturn,
    );
    const info = returnTicketInfo && returnTicketInfo.info && JSON.parse(returnTicketInfo.info);
    return getMappingDataFromSegments({
      segments: info?.listSegment,
    } as any);
  }

  const info = sortOrderData[type === 'depart' ? 0 : 1]?.tickets[0].info
      && JSON.parse(sortOrderData[type === 'depart' ? 0 : 1].tickets[0].info);

  return getMappingDataFromSegments({
    segments: info?.listSegment,
  } as any);
};

export const getTicketDataFromOrder = ({ orderData, type }: { orderData: any, type: 'depart' | 'return' }) => {
  if (isEmpty(orderData)) return { segmentData: null };
  const sortOrderData = orderData.length > 1
    ? [...orderData]?.sort((x, y) => {
      if (x?.tickets?.[0]?.departureTime > y?.tickets?.[0]?.departureTime) {
        return 1;
      }
      return -1;
    })
    : orderData;

  const isRoundTripSameAirline = sortOrderData.length === 1
    && sortOrderData[0]?.tickets?.[0]?.route?.split('|')?.length > 1;
  const isRoundTrip = sortOrderData.length === 2 || isRoundTripSameAirline;

  let returnTicketInfo;
  let departTicketInfo;
  const customerInfo = sortOrderData[0]?.info && JSON.parse(sortOrderData[0]?.info);
  if (!isRoundTrip && type === 'return') {
    return {
      info: null,
      segmentData: null,
    };
  }

  if (!isRoundTrip) {
    departTicketInfo = sortOrderData[0]?.tickets[0].info
      && JSON.parse(sortOrderData[0]?.tickets[0].info);

    return {
      tickets: sortOrderData[0]?.tickets,
      info: departTicketInfo,
      customerInfo,
      roundCode: sortOrderData[type === 'depart' ? 0 : 1]?.tickets[0]?.route?.split('|')?.[0],
    };
  }

  if (isRoundTripSameAirline) {
    const ticket = sortOrderData[0]?.tickets[0];
    const keyCodeDepart = ticket?.route?.split('|')?.[0];
    const keyCodeReturn = ticket?.route?.split('|')?.[1];
    if (type === 'depart') {
      departTicketInfo = sortOrderData[0]?.tickets.filter(
        (t: any) => t?.fromLocation + t.toLocation === keyCodeDepart,
      );
      const info = departTicketInfo[0]
        && departTicketInfo[0].info
        && JSON.parse(departTicketInfo[0].info);
      return {
        info,
        customerInfo,
        tickets: departTicketInfo,
        roundCode: keyCodeDepart,
        keyCodeReturn,
      };
    }
    returnTicketInfo = sortOrderData[0]?.tickets.filter(
      (t: any) => t?.fromLocation + t.toLocation === keyCodeReturn,
    );
    const info = returnTicketInfo[0]
      && returnTicketInfo[0].info
      && JSON.parse(returnTicketInfo[0].info);
    return {
      info,
      customerInfo,
      tickets: returnTicketInfo,
      roundCode: keyCodeReturn,
    };
  }

  const info = sortOrderData[type === 'depart' ? 0 : 1]?.tickets[0].info
    && JSON.parse(sortOrderData[type === 'depart' ? 0 : 1].tickets[0].info);

  return {
    info,
    tickets: sortOrderData[type === 'depart' ? 0 : 1]?.tickets,
    customerInfo,
    roundCode: sortOrderData[type === 'depart' ? 0 : 1]?.tickets[0]?.route?.split('|')?.[0],
  };
};
